import { useEffect, useId, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import { HideIcon } from "../../materials/icons/hide-icon/HideIcon";
import { ViewIcon } from "../../materials/icons/view-icon/ViewIcon";
import { Copyable } from "../copyable/Copyable";
import { IconButton } from "../icon-button/IconButton.Parts";
import { Tooltip } from "../tooltip/Tooltip";
import {
  ActionButtons,
  InnerInput,
  InnerWrapper,
  Props,
  Wrapper,
  text,
} from "./Input.Parts";

export const Input = ({
  label,
  id: idProp,
  type,
  hasError,
  disabled,
  readOnly,
  fullWidth,
  emulateFocus,
  placeholder,
  defaultValue,
  value,
  className,
  style,
  copyable,
  customActions,
  onChange,
  onBlur,
  ref,
  ...inputProps
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useId();
  const id = idProp || generatedId;

  const [showPassword, setShowPassword] = useState(false);
  const [hasContent, setHasContent] = useState(
    Boolean(defaultValue) || Boolean(value) || Boolean(placeholder),
  );

  // This effect handles the initial render and programmatic value changes
  useEffect(() => {
    setHasContent(
      Boolean(defaultValue) ||
        Boolean(value) ||
        Boolean(placeholder) ||
        Boolean(inputRef.current?.value) ||
        Boolean(inputRef.current?.defaultValue),
    );
  }, [
    value,
    placeholder,
    defaultValue,
    inputRef.current?.value,
    inputRef.current?.defaultValue,
  ]);

  useEffect(() => {
    if (emulateFocus && !disabled) {
      inputRef.current?.focus();
    }
  }, [emulateFocus, disabled]);

  return (
    <Wrapper
      $disabled={disabled}
      $hasError={hasError}
      $fullWidth={fullWidth}
      $emulateFocus={emulateFocus}
      $hasContent={hasContent}
      onClick={() => {
        !disabled && inputRef.current?.focus();
      }}
      className={className}
      style={style}
    >
      <InnerWrapper>
        <label htmlFor={id}>{label}</label>
        <InnerInput
          {...inputProps}
          ref={mergeRefs([ref, inputRef])}
          disabled={disabled}
          readOnly={readOnly}
          $hasContent={hasContent}
          $emulateFocus={emulateFocus}
          type={showPassword ? "text" : type}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={(e) => {
            setHasContent(e.target.value !== "" || !!placeholder);
            onChange?.(e);
          }}
          onBlur={(e) => {
            if (emulateFocus && !disabled) {
              inputRef.current?.focus();
            }
            onBlur?.(e);
          }}
          id={id}
        />
      </InnerWrapper>
      {(type === "password" ||
        (copyable && inputRef.current?.value) ||
        customActions) && (
        <ActionButtons>
          {type === "password" && (
            <IconButton
              type="button"
              disabled={disabled}
              onClick={() => setShowPassword((prev) => !prev)}
              $height="1.5rem"
              $width="1.5rem"
            >
              <Tooltip tip={showPassword ? text.tooltipHide : text.tooltipShow}>
                {showPassword ? <ViewIcon /> : <HideIcon />}
              </Tooltip>
            </IconButton>
          )}
          {copyable && inputRef.current?.value && (
            <Copyable iconSize="1.5rem" copyText={inputRef.current?.value} />
          )}
          {customActions &&
            customActions.map((action, index) => (
              <IconButton
                type="button"
                disabled={
                  typeof action.disabled === "function"
                    ? action.disabled(inputRef.current?.value ?? "")
                    : action.disabled
                }
                onClick={() => action.onClick(inputRef.current?.value ?? "")}
                $height="1.5rem"
                $width="1.5rem"
                key={index}
              >
                {action.icon}
              </IconButton>
            ))}
        </ActionButtons>
      )}
    </Wrapper>
  );
};
